import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Building2, Heart, Factory, ShoppingCart, 
  Zap, Users, Plane, Truck 
} from 'lucide-react';

const Industries = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const industries = [
    {
      icon: <Building2 />,
      title: 'Banking & Financial Services',
      description: 'Digital banking, risk management, and regulatory compliance solutions',
      solutions: ['Core Banking Modernization', 'Digital Payments', 'Risk Analytics', 'RegTech Solutions'],
      stats: '200+ Banks Transformed'
    },
    {
      icon: <Heart />,
      title: 'Healthcare & Life Sciences',
      description: 'Patient-centric solutions and clinical research platforms',
      solutions: ['Electronic Health Records', 'Telemedicine', 'Clinical Trials', 'Medical Imaging'],
      stats: '150+ Healthcare Organizations'
    },
    {
      icon: <Factory />,
      title: 'Manufacturing',
      description: 'Smart manufacturing and Industry 4.0 implementations',
      solutions: ['IoT Integration', 'Predictive Maintenance', 'Supply Chain Optimization', 'Quality Management'],
      stats: '300+ Manufacturing Plants'
    },
    {
      icon: <ShoppingCart />,
      title: 'Retail & Consumer',
      description: 'Omnichannel experiences and customer engagement platforms',
      solutions: ['E-commerce Platforms', 'Customer Analytics', 'Inventory Management', 'Personalization'],
      stats: '100+ Retail Brands'
    },
    {
      icon: <Zap />,
      title: 'Energy & Utilities',
      description: 'Smart grid solutions and renewable energy management',
      solutions: ['Smart Grid Technology', 'Energy Trading', 'Asset Management', 'Sustainability Analytics'],
      stats: '50+ Utility Companies'
    },
    {
      icon: <Users />,
      title: 'Government & Public Sector',
      description: 'Citizen services and digital governance solutions',
      solutions: ['Digital Identity', 'E-Governance', 'Smart Cities', 'Public Safety'],
      stats: '25+ Government Agencies'
    },
    {
      icon: <Plane />,
      title: 'Travel & Hospitality',
      description: 'Enhanced customer experiences and operational efficiency',
      solutions: ['Booking Systems', 'Revenue Management', 'Customer Experience', 'Loyalty Programs'],
      stats: '75+ Travel Companies'
    },
    {
      icon: <Truck />,
      title: 'Logistics & Transportation',
      description: 'Supply chain optimization and fleet management solutions',
      solutions: ['Fleet Management', 'Route Optimization', 'Warehouse Automation', 'Last-Mile Delivery'],
      stats: '120+ Logistics Partners'
    }
  ];

  return (
    <section id="industries" className="industries" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Industry Expertise</h2>
          <p>
            Deep domain knowledge and proven solutions across key industries, 
            helping organizations navigate sector-specific challenges and opportunities.
          </p>
        </motion.div>

        <div className="industries-grid">
          {industries.map((industry, index) => (
            <motion.div
              key={industry.title}
              className="industry-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="industry-header">
                <div className="industry-icon">
                  {industry.icon}
                </div>
                <div className="industry-info">
                  <h3>{industry.title}</h3>
                  <p>{industry.description}</p>
                </div>
              </div>
              
              <div className="industry-solutions">
                <h4>Key Solutions</h4>
                <ul>
                  {industry.solutions.map(solution => (
                    <li key={solution}>{solution}</li>
                  ))}
                </ul>
              </div>
              
              <div className="industry-stats">
                <span className="stats-number">{industry.stats}</span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="industry-showcase">
          <motion.div 
            className="showcase-content"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h3>Cross-Industry Innovation</h3>
            <p>
              Our solutions transcend industry boundaries, leveraging best practices 
              and emerging technologies to create competitive advantages across sectors.
            </p>
            <div className="innovation-areas">
              <div className="innovation-item">
                <span>Digital Ecosystems</span>
              </div>
              <div className="innovation-item">
                <span>Sustainable Technology</span>
              </div>
              <div className="innovation-item">
                <span>Human-Centric Design</span>
              </div>
              <div className="innovation-item">
                <span>Ethical AI</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Industries;